/**
 * PromotionCreationForm
 *
 * SCRUM-1285: Guide Dashboard — Promotions Creation UI
 * Full creation form for promotions. Supports percentage/fixed_amount/free_addon
 * discount types, public/private toggle, booking/trip windows, usage limits,
 * and eligibility constraints. New promotions created in draft status.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tag, Plus, Loader2, AlertCircle, X, Eye, EyeOff } from 'lucide-react';
import { getPromotions, createPromotion } from '../../../services/guidePricingApi';
import { parseDollarsToCents, formatCentsToDollars } from '../../../utils/formHelpers';

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage Off' },
  { value: 'fixed_amount', label: 'Fixed Amount Off' },
  { value: 'free_addon', label: 'Free Add-on' }
];

const INITIAL_FORM = {
  name: '',
  code: '',
  promotionType: 'percentage',
  discountValue: '',
  isPublic: true,
  bookingStartDate: '',
  bookingEndDate: '',
  tripStartDate: '',
  tripEndDate: '',
  maxRedemptions: '',
  maxRedemptionsPerCustomer: '',
  minPartySize: '',
  minBookingAmountDollars: ''
};

function validateForm(form) {
  if (!form.name.trim()) {
    return { field: 'name', message: 'Promotion name is required' };
  }

  if (!form.isPublic && form.code && !/^[A-Z0-9_-]+$/i.test(form.code)) {
    return { field: 'code', message: 'Code must be alphanumeric with dashes/underscores' };
  }

  if (form.promotionType !== 'free_addon') {
    const val = parseFloat(form.discountValue);
    if (isNaN(val) || val <= 0) {
      return { field: 'discountValue', message: 'Discount value must be a positive number' };
    }
    if (form.promotionType === 'percentage' && val > 100) {
      return { field: 'discountValue', message: 'Percentage discount cannot exceed 100%' };
    }
  }

  if (form.bookingStartDate && form.bookingEndDate) {
    if (new Date(form.bookingEndDate) <= new Date(form.bookingStartDate)) {
      return { field: 'bookingEndDate', message: 'Booking end date must be after start date' };
    }
  }

  if (form.tripStartDate && form.tripEndDate) {
    if (new Date(form.tripEndDate) <= new Date(form.tripStartDate)) {
      return { field: 'tripEndDate', message: 'Trip end date must be after start date' };
    }
  }

  if (form.maxRedemptions && (parseInt(form.maxRedemptions, 10) <= 0 || isNaN(parseInt(form.maxRedemptions, 10)))) {
    return { field: 'maxRedemptions', message: 'Max redemptions must be a positive number' };
  }

  if (form.maxRedemptionsPerCustomer && (parseInt(form.maxRedemptionsPerCustomer, 10) <= 0 || isNaN(parseInt(form.maxRedemptionsPerCustomer, 10)))) {
    return { field: 'maxRedemptionsPerCustomer', message: 'Max per customer must be a positive number' };
  }

  if (form.minPartySize && (parseInt(form.minPartySize, 10) <= 0 || isNaN(parseInt(form.minPartySize, 10)))) {
    return { field: 'minPartySize', message: 'Minimum party size must be a positive number' };
  }

  if (form.minBookingAmountDollars) {
    const dollars = parseFloat(form.minBookingAmountDollars);
    if (isNaN(dollars) || dollars < 0) {
      return { field: 'minBookingAmountDollars', message: 'Minimum booking amount must be non-negative' };
    }
  }

  return null;
}

export default function PromotionCreationForm({ experienceId }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getPromotions(experienceId);
      setPromotions(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setFormError(null);
    setFieldError(null);
    setShowForm(false);
  };

  const openForm = () => {
    setForm(INITIAL_FORM);
    setFormError(null);
    setFieldError(null);
    setShowForm(true);
  };

  const updateField = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (fieldError?.field === field) {
      setFieldError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm(form);
    if (validationError) {
      setFieldError(validationError);
      setFormError(validationError.message);
      return;
    }

    // Check code uniqueness against existing promotions (private only)
    if (!form.isPublic && form.code) {
      const codeUpper = form.code.toUpperCase();
      const duplicate = promotions.find(p => p.code === codeUpper);
      if (duplicate) {
        setFieldError({ field: 'code', message: 'This code is already in use for this experience' });
        setFormError('This code is already in use for this experience');
        return;
      }
    }

    setSaving(true);
    setFormError(null);
    setFieldError(null);

    const payload = {
      name: form.name.trim(),
      promotionType: form.promotionType,
      isPublic: form.isPublic
    };

    // Only include code for private promotions (or if explicitly provided)
    if (!form.isPublic && form.code.trim()) {
      payload.code = form.code.trim();
    }

    if (form.promotionType !== 'free_addon') {
      payload.discountValue = parseFloat(form.discountValue);
      if (form.promotionType === 'percentage') {
        payload.discountUnit = 'percentage';
      } else {
        payload.discountUnit = 'cents';
      }
    } else {
      payload.discountValue = 0;
    }

    if (form.bookingStartDate) { payload.bookingStartDate = form.bookingStartDate; }
    if (form.bookingEndDate) { payload.bookingEndDate = form.bookingEndDate; }
    if (form.tripStartDate) { payload.tripStartDate = form.tripStartDate; }
    if (form.tripEndDate) { payload.tripEndDate = form.tripEndDate; }
    if (form.maxRedemptions) { payload.maxRedemptions = parseInt(form.maxRedemptions, 10); }
    if (form.maxRedemptionsPerCustomer) { payload.maxRedemptionsPerCustomer = parseInt(form.maxRedemptionsPerCustomer, 10); }
    if (form.minPartySize) { payload.minPartySize = parseInt(form.minPartySize, 10); }
    if (form.minBookingAmountDollars) {
      payload.minBookingValueCents = parseDollarsToCents(form.minBookingAmountDollars);
    }

    try {
      await createPromotion(experienceId, payload);
      resetForm();
      await fetchPromotions();
    } catch (err) {
      const apiError = err.error?.message || err.message || 'Failed to create promotion';
      const apiDetails = err.error?.details;

      if (apiDetails && Array.isArray(apiDetails)) {
        const firstIssue = apiDetails[0];
        if (firstIssue?.path) {
          setFieldError({ field: firstIssue.path[0], message: firstIssue.message });
        }
      }

      setFormError(apiError);
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      expired: 'bg-red-100 text-red-700',
      ended: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDiscount = (promo) => {
    if (promo.promotion_type === 'percentage') {
      return `-${parseFloat(promo.discount_value)}%`;
    }
    if (promo.promotion_type === 'fixed_amount') {
      return `-${formatCentsToDollars(parseFloat(promo.discount_value))}`;
    }
    return 'Free add-on';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Promotions</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="promotions-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Promotions</h3>
        </div>
        {!showForm && (
          <button
            onClick={openForm}
            className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            data-testid="add-promotion-btn"
          >
            <Plus className="h-4 w-4" />
            Create Promotion
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg border" data-testid="promotion-form">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm text-gray-700">Create Promotion</h4>
            <button type="button" onClick={resetForm} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Promotion Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., Summer Special 20% Off"
              className={`w-full border rounded-md px-3 py-2 text-sm ${fieldError?.field === 'name' ? 'border-red-500' : ''}`}
              maxLength={100}
              data-testid="promotion-name-input"
            />
          </div>

          {/* Discount Type + Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.promotionType}
                onChange={(e) => updateField('promotionType', e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="promotion-type-select"
              >
                {DISCOUNT_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
            </div>
            {form.promotionType !== 'free_addon' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Value <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={form.discountValue}
                    onChange={(e) => updateField('discountValue', e.target.value)}
                    placeholder={form.promotionType === 'percentage' ? 'e.g., 20' : 'e.g., 25.00'}
                    className={`w-full border rounded-md px-3 py-2 text-sm ${fieldError?.field === 'discountValue' ? 'border-red-500' : ''}`}
                    data-testid="promotion-discount-input"
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-400">
                    {form.promotionType === 'percentage' ? '%' : '$'}
                  </span>
                </div>
                {fieldError?.field === 'discountValue' && (
                  <p className="text-xs text-red-600 mt-1" data-testid="discount-value-error">{fieldError.message}</p>
                )}
              </div>
            )}
          </div>

          {/* Public/Private Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => updateField('isPublic', true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm border ${
                  form.isPublic
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="promotion-public-btn"
              >
                <Eye className="h-4 w-4" />
                Public (auto-apply)
              </button>
              <button
                type="button"
                onClick={() => updateField('isPublic', false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm border ${
                  !form.isPublic
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid="promotion-private-btn"
              >
                <EyeOff className="h-4 w-4" />
                Private (code required)
              </button>
            </div>
          </div>

          {/* Promo Code — only for private promotions */}
          {!form.isPublic && (
            <div className="mb-4" data-testid="promo-code-field">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Promo Code
              </label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => updateField('code', e.target.value.toUpperCase())}
                placeholder="Leave blank to auto-generate"
                className={`w-full border rounded-md px-3 py-2 text-sm uppercase ${fieldError?.field === 'code' ? 'border-red-500' : ''}`}
                maxLength={50}
                data-testid="promotion-code-input"
              />
              {fieldError?.field === 'code' && (
                <p className="text-xs text-red-600 mt-1">{fieldError.message}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">Alphanumeric, dashes, and underscores only</p>
            </div>
          )}

          {/* Booking Window */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking Start Date</label>
              <input
                type="date"
                value={form.bookingStartDate}
                onChange={(e) => updateField('bookingStartDate', e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="booking-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Booking End Date</label>
              <input
                type="date"
                value={form.bookingEndDate}
                onChange={(e) => updateField('bookingEndDate', e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-sm ${fieldError?.field === 'bookingEndDate' ? 'border-red-500' : ''}`}
                data-testid="booking-end-date"
              />
              {fieldError?.field === 'bookingEndDate' && (
                <p className="text-xs text-red-600 mt-1" data-testid="booking-end-date-error">{fieldError.message}</p>
              )}
            </div>
          </div>

          {/* Trip Window */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip Start Date</label>
              <input
                type="date"
                value={form.tripStartDate}
                onChange={(e) => updateField('tripStartDate', e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="trip-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip End Date</label>
              <input
                type="date"
                value={form.tripEndDate}
                onChange={(e) => updateField('tripEndDate', e.target.value)}
                className={`w-full border rounded-md px-3 py-2 text-sm ${fieldError?.field === 'tripEndDate' ? 'border-red-500' : ''}`}
                data-testid="trip-end-date"
              />
              {fieldError?.field === 'tripEndDate' && (
                <p className="text-xs text-red-600 mt-1">{fieldError.message}</p>
              )}
            </div>
          </div>

          {/* Usage Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Total Redemptions</label>
              <input
                type="number"
                min="1"
                value={form.maxRedemptions}
                onChange={(e) => updateField('maxRedemptions', e.target.value)}
                placeholder="Unlimited"
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="max-redemptions-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Per Customer
              </label>
              <input
                type="number"
                min="1"
                value={form.maxRedemptionsPerCustomer}
                onChange={(e) => updateField('maxRedemptionsPerCustomer', e.target.value)}
                placeholder="Unlimited"
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="max-per-customer-input"
              />
              {form.maxRedemptionsPerCustomer === '1' && (
                <p className="text-xs text-amber-600 mt-1" data-testid="first-time-label">
                  First-time customer discount (limit 1 per customer)
                </p>
              )}
            </div>
          </div>

          {/* Eligibility Constraints */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Party Size</label>
              <input
                type="number"
                min="1"
                value={form.minPartySize}
                onChange={(e) => updateField('minPartySize', e.target.value)}
                placeholder="No minimum"
                className="w-full border rounded-md px-3 py-2 text-sm"
                data-testid="min-party-size-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Booking Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-sm text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.minBookingAmountDollars}
                  onChange={(e) => updateField('minBookingAmountDollars', e.target.value)}
                  placeholder="No minimum"
                  className="w-full border rounded-md pl-7 pr-3 py-2 text-sm"
                  data-testid="min-booking-amount-input"
                />
              </div>
            </div>
          </div>

          {/* Draft status note */}
          <div className="mb-4 p-2 bg-amber-50 text-amber-700 rounded text-sm" data-testid="draft-status-note">
            New promotions are created as <strong>draft</strong>. You can activate them after review.
          </div>

          {/* Form error */}
          {formError && (
            <div className="mb-4 p-2 bg-red-50 text-red-700 rounded text-sm flex items-center gap-2" data-testid="promotion-form-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {formError}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="promotion-save-btn"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              Create Promotion
            </button>
          </div>
        </form>
      )}

      {/* Existing Promotions List */}
      {promotions.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="promotions-empty">
          <Tag className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No promotions yet</p>
          <p className="text-xs text-gray-400 mt-1">Create promotions to offer discounts to customers</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="promotions-list">
          {promotions.map(promo => (
            <div
              key={promo.id}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              data-testid="promotion-row"
            >
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(promo.status)}`}>
                  {promo.status}
                </span>
                <span className="text-sm font-medium text-gray-900">{promo.name}</span>
                <span className="text-sm text-emerald-600 font-medium" data-testid="promotion-discount">
                  {formatDiscount(promo)}
                </span>
                {promo.code && (
                  <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                    {promo.code}
                  </span>
                )}
                {promo.is_public && (
                  <span className="text-xs text-blue-500">
                    <Eye className="h-3 w-3 inline" /> Public
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
